import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { Check, Loader2, XCircle, ArrowLeft } from "lucide-react";

export function BillingSuccess() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useSubscription();
  const [status, setStatus] = useState("polling"); // polling | paid | failed
  const attempts = useRef(0);

  useEffect(() => {
    const sid = params.get("token") || params.get("session_id");
    if (!sid) { setStatus("failed"); return; }
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      attempts.current += 1;
      try {
        const r = await api.get(`/billing/status/${sid}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          setTimeout(() => nav("/dashboard", { replace: true }), 1500);
          return;
        }
        if (["failed","expired","canceled","failure"].includes(r.data.payment_status)) {
          setStatus("failed"); return;
        }
      } catch {}
      if (attempts.current >= 20) { setStatus("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; };
  }, [params, nav, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{background:"var(--bg)"}} data-testid="billing-success">
      <div className="max-w-md w-full text-center">
        {status === "polling" && (
          <>
            <Loader2 size={48} className="mx-auto animate-spin" style={{color:"var(--gold)"}} />
            <h1 className="font-display text-3xl mt-6">{t("billing.success_subtitle")}</h1>
            <p className="text-neutral-500 mt-4 text-sm">{t("common.loading")}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto border-2 flex items-center justify-center" style={{borderColor:"var(--gold)"}}>
              <Check size={32} style={{color:"var(--gold)"}} />
            </div>
            <h1 className="font-display text-4xl mt-6" style={{color:"var(--gold)"}}>{t("billing.success_title")}</h1>
            <p className="text-neutral-400 mt-4">{t("billing.success_ready")}</p>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle size={48} className="mx-auto text-red-400" />
            <h1 className="font-display text-3xl mt-6">{t("common.error")}</h1>
            <p className="text-neutral-400 mt-4 text-sm">{t("billing.cancel_subtitle")}</p>
            <Link to="/pricing" className="btn-primary-gold inline-flex mt-8 items-center gap-2" data-testid="back-to-pricing">
              <ArrowLeft size={14} /> {t("billing.try_again")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function BillingCancel() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{background:"var(--bg)"}} data-testid="billing-cancel">
      <div className="max-w-md w-full text-center">
        <XCircle size={48} className="mx-auto text-neutral-500" />
        <h1 className="font-display text-3xl mt-6">{t("billing.cancel_title")}</h1>
        <p className="text-neutral-400 mt-4">{t("billing.cancel_subtitle")}</p>
        <Link to="/pricing" className="btn-primary-gold inline-flex mt-8 items-center gap-2" data-testid="billing-cancel-retry">
          <ArrowLeft size={14} /> {t("billing.try_again")}
        </Link>
      </div>
    </div>
  );
}
